import { Component, inject } from "@angular/core";
import { CommonModule } from "@angular/common";
import { Router } from "@angular/router";
import { AppTableComponent } from "../../../../Common/Components/Table/TableComponent";
import { TableColumn, TableConfig, TableActionConfig, TablePagination } from "../../../../Common/Components/Table/Type/TableType";

interface Role {
  id: string;
  name: string;
  description: string;
  isSystem: boolean;
  createdAt: string;
}

interface User {
  id: string;
  name: string;
  email: string;
  role: {
    id: string;
    name: string;
    description: string;
    isSystem: boolean;
  };
  createdAt: string;
}

@Component({
  selector: "app-test-table-page",
  standalone: true,
  imports: [CommonModule, AppTableComponent],
  templateUrl: "./TestTablePage.html",
  styleUrl: "./TestTablePage.scss",
})
export class TestTablePage {
  private readonly router = inject(Router);
  // Mock roles data
  rolesData: Role[] = [
    {
      id: "6957e5f5b6755424c54321b5",
      name: "superadmin",
      description: "Full access",
      isSystem: true,
      createdAt: "2026-01-02T15:36:21.428Z",
    },
    {
      id: "6957e6c1c8ff78d765a47143",
      name: "admin",
      description: "",
      isSystem: false,
      createdAt: "2026-01-02T15:39:45.054Z",
    },
    {
      id: "6957e6c1c8ff78d765a47148",
      name: "user",
      description: "",
      isSystem: false,
      createdAt: "2026-01-02T15:39:45.060Z",
    },
  ];

  // Mock users data
  usersData: User[] = [
    {
      id: "6957e5f5b6755424c54321ba",
      name: "Super Admin",
      email: "superadmin@mailboxclubindonesia.org",
      role: {
        id: "6957e5f5b6755424c54321b5",
        name: "superadmin",
        description: "Full access",
        isSystem: true,
      },
      createdAt: "2026-01-02T15:36:21.511Z",
    },
    {
      id: "6957e6c1c8ff78d765a47173",
      name: "Roosevelt Veum",
      email: "cecelia_schroeder@mailboxclubindonesia.org",
      role: {
        id: "6957e6c1c8ff78d765a47148",
        name: "user",
        description: "",
        isSystem: false,
      },
      createdAt: "2026-01-02T15:39:45.370Z",
    },
  ];

  // Roles table configuration
  rolesColumns: TableColumn<Role>[] = [
    { key: "id", header: "ID", sortable: true, width: "20%" },
    { key: "name", header: "Name", sortable: true, width: "15%" },
    { key: "description", header: "Description", sortable: true, width: "25%" },
    {
      key: "isSystem",
      header: "System Role",
      sortable: true,
      width: "15%",
      cellTemplate: (item) =>
        (() => {
          if (item.isSystem) {
            return "Yes";
          }
          return "No";
        })(),
    },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      width: "25%",
      cellTemplate: (item) => new Date(item.createdAt).toLocaleDateString(),
    },
  ];

  // Users table configuration
  usersColumns: TableColumn<User>[] = [
    { key: "id", header: "ID", sortable: true, width: "15%" },
    { key: "name", header: "Name", sortable: true, width: "20%" },
    { key: "email", header: "Email", sortable: true, width: "25%" },
    { key: "role.name", header: "Role", sortable: true, width: "15%" },
    {
      key: "createdAt",
      header: "Created",
      sortable: true,
      width: "25%",
      cellTemplate: (item) => new Date(item.createdAt).toLocaleDateString(),
    },
  ];

  // Roles table config
  rolesConfig: TableConfig<Role> = {
    loading: false,
    emptyMessage: "No roles found",
    showActions: true,
    showSearch: true,
    searchPlaceholder: "Search roles...",
    showCreate: true,
    createButtonLabel: "Create Role",
    createButtonIcon: "add",
    onCreate: () => this.createRole(),
    actionButtons: [
      {
        icon: "edit",
        label: "Edit Role",
        handler: (item: Role) => this.editRole(item),
        visible: (item: Role) => !item.isSystem, // Hide edit for system roles
      },
      {
        icon: "delete",
        label: "Delete Role",
        handler: (item: Role) => this.deleteRole(item),
        visible: (item: Role) => !item.isSystem, // Hide delete for system roles
      },
    ],
  };

  // Users table config
  usersConfig: TableConfig<User> = {
    loading: false,
    emptyMessage: "No users found",
    showActions: true,
    showSearch: true,
    searchPlaceholder: "Search users...",
    showCreate: true,
    createButtonLabel: "Create User",
    createButtonIcon: "person_add",
    onCreate: () => this.createUser(),
    actionButtons: [
      {
        icon: "edit",
        label: "Edit User",
        handler: (item: User) => this.editUser(item),
      },
      {
        icon: "delete",
        label: "Delete User",
        handler: (item: User) => this.deleteUser(item),
      },
    ],
  };

  // Mock pagination data
  rolesPagination: TablePagination = {
    page: 1,
    limit: 10,
    total: 3,
    pages: 1,
  };

  usersPagination: TablePagination = {
    page: 1,
    limit: 10,
    total: 21,
    pages: 3,
  };

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSortChange(sort: unknown): void {
    // Removed logging
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onPageChange(event: { page: number; search?: string }): void {
    // Removed logging
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onPageSizeChange(event: { limit: number; search?: string }): void {
    // Removed logging
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onActionClick<T>(event: { action: TableActionConfig<T>; item: T }): void {
    // Removed logging
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onSearchChange(event: { term: string; page: number }): void {
    // Removed logging
  }

  createRole(): void {
    this.router.navigate(["/roles/create"]).catch(() => undefined);
  }

  createUser(): void {
    // Removed logging
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  editRole(role: Role): void {
    // Removed logging
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  deleteRole(role: Role): void {
    // Removed logging
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  editUser(user: User): void {
    // Removed logging
  }

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  deleteUser(user: User): void {
    // Removed logging
  }
}
